import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, GraduationCap, UserCheck, UserPlus, CalendarClock, CheckCircle2, Coins, TrendingDown } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { EmptyState, PageHeader, PageLoader, Pagination, Panel, SearchInput, StatCard, StatusBadge } from "@/components/common";

export function AdminDashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <PageLoader />;
  return (
    <div>
      <PageHeader title="Admin Dashboard" subtitle="Platform overview at a glance." />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 lg:gap-6">
        <StatCard label="Total Students" value={s.total_students} icon={Users} testid="stat-total-students" />
        <StatCard label="Total Tutors" value={s.total_tutors} icon={GraduationCap} testid="stat-total-tutors" />
        <StatCard label="Pending Tutor Approvals" value={s.pending_tutors} icon={UserCheck} tone="amber" testid="stat-pending-tutors" />
        <StatCard label="Pending Student Approvals" value={s.pending_students} icon={UserPlus} tone="amber" testid="stat-pending-students" />
        <StatCard label="Upcoming Sessions" value={s.upcoming_sessions} icon={CalendarClock} tone="cyan" testid="stat-upcoming-sessions" />
        <StatCard label="Completed Sessions" value={s.completed_sessions} icon={CheckCircle2} tone="green" testid="stat-completed-sessions" />
        <StatCard label="Credits Allocated" value={s.credits_allocated} icon={Coins} tone="navy" testid="stat-credits-allocated" />
        <StatCard label="Credits Consumed" value={s.credits_consumed} icon={TrendingDown} tone="green" testid="stat-credits-consumed" hint={`${s.credits_reserved} reserved`} />
      </div>
      <Panel className="mt-8 p-6">
        <div className="mb-4 flex items-center justify-between"><h3 className="text-lg font-bold text-[#0B1A33]">Next sessions</h3>
          <Link to="/admin/meetings" className="text-sm font-semibold text-[#1F6FFF]" data-testid="dashboard-view-meetings">View all</Link></div>
        {s.upcoming.length === 0 ? <EmptyState title="No upcoming sessions" /> : s.upcoming.map((x) => (
          <div key={x.id} className="flex items-center justify-between border-b border-slate-100 py-3 last:border-0">
            <div><p className="font-semibold text-[#0B1A33]">{x.title}</p><p className="text-sm text-slate-500">{x.tutor_name} · {x.student_names.length} student(s) · {fmtDateTime(x.start_at)}</p></div>
            <StatusBadge status={x.status} />
          </div>
        ))}
      </Panel>
    </div>
  );
}

export function SessionsAdmin({ scope }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  useEffect(() => { setStatus("all"); setPage(1); }, [scope]);
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/sessions", { params: { scope, q, page, status: status === "all" ? "" : status } });
    setData(data);
  }, [scope, q, page, status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const act = async (url, body) => { try { const r = await api.post(url, body); toast.success(r.data.message); load(); } catch (e) { toast.error(errMsg(e)); } };
  const options = scope === "upcoming" ? ["all", "scheduled", "in_progress"] : ["all", "completed", "cancelled"];
  return (
    <div>
      <PageHeader title={scope === "upcoming" ? "Scheduled Meetings" : "Session History"} subtitle="All sessions across the platform with meeting and credit details." />
      <Panel>
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row">
          <SearchInput value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Title, tutor, student, meeting ID" testid="sessions-search-input" />
          <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
            <SelectTrigger className="h-10 rounded-full sm:w-48" data-testid="sessions-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o === "all" ? "All statuses" : o.replace("_", " ")}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {!data ? <PageLoader /> : data.items.length === 0 ? <div className="p-6"><EmptyState title="No sessions found" /></div> : (
          <div className="overflow-x-auto">
            <Table data-testid="admin-sessions-table">
              <TableHeader><TableRow>
                <TableHead>Session</TableHead><TableHead>Tutor</TableHead><TableHead>Students</TableHead><TableHead>Date & time</TableHead>
                <TableHead>Duration</TableHead><TableHead>Meeting ID</TableHead><TableHead>Status</TableHead><TableHead>Credits</TableHead><TableHead />
              </TableRow></TableHeader>
              <TableBody>
                {data.items.map((s) => {
                  const ded = s.participants.filter((p) => p.credit_status === "deducted").length;
                  return (
                    <TableRow key={s.id} data-testid={`admin-session-row-${s.id}`}>
                      <TableCell><p className="font-semibold text-[#0B1A33]">{s.title}</p><p className="text-xs text-slate-500">{s.subject}</p></TableCell>
                      <TableCell>{s.tutor_name}</TableCell>
                      <TableCell>
                        <Popover><PopoverTrigger asChild><button className="text-sm font-semibold text-[#1F6FFF]" data-testid={`admin-session-participants-${s.id}`}>{s.participants.length} student(s)</button></PopoverTrigger>
                          <PopoverContent className="w-72 space-y-2">
                            {s.participants.map((p) => (
                              <div key={p.id} className="flex items-center justify-between text-sm"><span>{p.student_name}</span>
                                <span className="flex gap-1"><StatusBadge status={p.attendance} /><StatusBadge status={p.credit_status} /></span></div>
                            ))}
                          </PopoverContent></Popover>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">{fmtDateTime(s.start_at)}</TableCell>
                      <TableCell>{s.duration} min</TableCell>
                      <TableCell><span className="font-mono text-xs">{s.meeting?.meeting_id}</span><span className="block text-[10px] uppercase text-slate-400">{s.meeting?.provider}</span></TableCell>
                      <TableCell><StatusBadge status={s.status} testid={`admin-session-status-${s.id}`} /></TableCell>
                      <TableCell className="text-sm" data-testid={`admin-session-credits-${s.id}`}>{s.status === "completed" ? `${ded} deducted` : s.status === "cancelled" ? "Released" : "Reserved"}</TableCell>
                      <TableCell>
                        {s.status === "scheduled" && <Button size="sm" variant="outline" className="text-red-600" data-testid={`admin-cancel-session-${s.id}`} onClick={() => act(`/sessions/${s.id}/cancel`, { reason: "Cancelled by admin" })}>Cancel</Button>}
                        {s.status === "in_progress" && <Button size="sm" variant="outline" data-testid={`admin-complete-session-${s.id}`} onClick={() => act(`/sessions/${s.id}/complete`)}>Complete</Button>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Pagination page={data.page} pages={data.pages} total={data.total} onPage={setPage} />
          </div>
        )}
      </Panel>
    </div>
  );
}
